import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDiscount } from './hooks/useDiscount'; // Import the hook
import { DISCOUNT_AMOUNT_TOOMANS } from './config/discountConfig';

export default function Header() {
  const { isDiscountActive, getRemainingTime } = useDiscount();
  const [remainingTime, setRemainingTime] = useState(getRemainingTime());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isDiscountActive) {
      const timer = setInterval(() => {
        setRemainingTime(getRemainingTime());
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isDiscountActive, getRemainingTime]);

  // Hide header on scroll down, show on scroll up
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
      const currentScrollY = window.scrollY;
          if (currentScrollY < 64) {
        setIsVisible(true);
          } else if (currentScrollY > lastScrollY) {
            setIsVisible(false);
      } else {
            setIsVisible(true);
      }
      setLastScrollY(currentScrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Helper to scroll to section
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Handler for nav links that scroll
  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    if (location.pathname === '/') {
      scrollToSection(sectionId);
    } else {
      navigate('/', { replace: false });
      // Wait for navigation, then scroll
      setTimeout(() => scrollToSection(sectionId), 350);
    }
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { to: '/', label: 'خانه', section: 'home' },
    { to: '/products', label: 'محصولات' },
    { to: '/blog', label: 'وبلاگ' },
    { to: '/ai-tools', label: 'بهترین AI برای شما' },
  ];

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    const body = document.body;
    const originalOverflow = body.style.overflow;
    const originalPaddingRight = body.style.paddingRight;

    if (mobileMenuOpen) {
      const scrollbarWidth = window.innerWidth - body.clientWidth;
      body.style.overflow = 'hidden';
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      body.style.overflow = originalOverflow;
      body.style.paddingRight = originalPaddingRight;
    };
  }, [mobileMenuOpen]);

  return (
    <>
      {isDiscountActive && (
        <div 
          className="fixed top-0 left-0 w-full text-white p-3 text-center shadow-lg"
          style={{ zIndex: 999999, backgroundColor: '#dc2626' }}
        >
          <p className="text-sm md:text-base">
            تخفیف ویژه و یک ماه رایگان grok4 برای اولین بازدید شما، سریع عمل کنید زمان باقی مانده {' '}
            <span className="font-bold" dir="ltr" style={{ color: '#ffffff' }}>
              {`${String(remainingTime.days).padStart(2, '0')}:${String(remainingTime.hours).padStart(2, '0')}:${String(remainingTime.minutes).padStart(2, '0')}:${String(remainingTime.seconds).padStart(2, '0')}`}
            </span>{' '}
          </p>
        </div>
      )}
      {/* HEADER: Glassmorphism */}
      <header 
        className="fixed top-4 left-1/2 z-50 glassmorph border border-white/20 shadow-lg backdrop-blur-lg"
        style={{
          transform: `translateX(-50%) translateY(${isVisible ? '0' : '-150%'})`,
          top: isDiscountActive ? '4rem' : '1rem', // Increased top position when banner is active
          width: 'calc(100% - 32px)',
          maxWidth: '1200px',
          padding: '0 1rem',
          height: '68px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderRadius: '2rem',
          transition: 'transform 0.35s cubic-bezier(.4,0,.2,1)',
          flexDirection: 'row',
        }}
      >
        {/* Burger + Start Button (right) - now first in DOM, right side */}
        <div className="flex items-center gap-2" style={{order: 1}}>
          <button
            className="ml-2 flex items-center justify-center w-11 h-11 rounded-full hover:bg-white/20 transition focus:outline-none md:hidden"
            aria-label={mobileMenuOpen ? 'بستن منو' : 'باز کردن منو'}
            onClick={() => setMobileMenuOpen(v => !v)}
            style={{background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.2)'}}
          >
            {mobileMenuOpen ? (
              // Close icon
              <svg width="28" height="28" fill="none" stroke="#F5F5F5" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            ) : (
              // Hamburger icon
              <svg width="28" height="28" fill="none" stroke="#F5F5F5" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <line x1="3" y1="7" x2="21" y2="7" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="17" x2="21" y2="17" />
              </svg>
            )}
          </button>
          <button className="btn-primary px-6 py-2 text-base shadow transition-all duration-200 focus:outline-none rounded-xl font-bold bg-electric-blue text-offwhite hover:bg-[#005FCC]" style={{borderRadius: '1rem'}}>شروع کنید</button>
        </div>
        {/* Navigation Links (center, desktop only) */}
        <nav className="hidden md:flex flex-1 items-center justify-center gap-8" style={{order: 2}}>
          {navLinks.map(link => link.section ? (
            <a
              key={link.label}
              href={`#${link.section}`}
              onClick={e => handleNavClick(e, link.section)}
              className={`text-lg font-extrabold px-3 py-1 rounded transition-colors duration-200 text-offwhite hover:text-amber-400 ${location.pathname === '/' && link.section === 'home' ? 'text-amber-400 underline underline-offset-8' : ''}`}
              style={{textShadow: '0 1px 8px rgba(0,0,0,0.18)'}}
            >
              {link.label}
            </a>
          ) : (
            <Link
              key={link.to}
              to={link.to}
              className={`text-lg font-extrabold px-3 py-1 rounded transition-colors duration-200 text-offwhite hover:text-amber-400 ${location.pathname === link.to ? 'text-amber-400 underline underline-offset-8' : ''}`}
              style={{textShadow: '0 1px 8px rgba(0,0,0,0.18)'}}
            >
              {link.label}
            </Link>
          ))}
        </nav>
        {/* Logo (left) - now last in DOM, left side */}
        <div className="flex items-center gap-2 mr-5" style={{order: 3}}>
          <span className="text-offwhite font-extrabold text-2xl tracking-tight select-none" style={{letterSpacing: '-0.02em'}}>SharifGPT</span>
        </div>
      </header>
      {/* NAV OVERLAY MENU */}
      <nav
        className={`fixed inset-0 z-[9999] transition-opacity duration-300 ease-in-out flex flex-col items-end md:hidden ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        style={{
          background: mobileMenuOpen ? 'rgba(0,0,0,0.3)' : 'transparent',
          backdropFilter: mobileMenuOpen ? 'blur(8px)' : 'none',
          WebkitBackdropFilter: mobileMenuOpen ? 'blur(8px)' : 'none',
        }}
        role="dialog"
        aria-modal="true"
        tabIndex={mobileMenuOpen ? 0 : -1}
        onClick={() => setMobileMenuOpen(false)}
      >
        <div
          className={`flex flex-col h-full w-full max-w-md p-8 pt-24 gap-0 relative transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
          style={{
            background: 'rgba(255,255,255,0.80)',
            minHeight: '100vh',
            borderRadius: '2rem 0 0 2rem',
            boxShadow: '0 8px 32px 0 rgba(31,38,135,0.18)',
            border: '1.5px solid rgba(255,255,255,0.22)',
            backdropFilter: 'blur(32px) saturate(160%)',
            WebkitBackdropFilter: 'blur(32px) saturate(160%)',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Close icon (top right, redundant for accessibility) */}
          <button
            className="absolute top-6 right-8 w-11 h-11 flex items-center justify-center rounded-full hover:bg-zinc-200/80 transition focus:outline-none"
            aria-label="بستن منو"
            onClick={() => setMobileMenuOpen(false)}
          >
            <svg width="28" height="28" fill="none" stroke="#333" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          {/* Nav Links */}
          {navLinks.map(link => link.section ? (
            <a
              key={link.label}
              href={`#${link.section}`}
              className={`block w-full py-6 text-2xl font-extrabold text-gray-900 text-right border-b border-zinc-200 hover:text-[#007AFF] transition ${location.pathname === '/' && link.section === 'home' ? 'text-blue-600' : ''}`}
              onClick={e => handleNavClick(e, link.section)}
              style={{textShadow: '0 1px 8px rgba(255,255,255,0.18)'}}
            >
              {link.label}
            </a>
          ) : (
            <Link
              key={link.to}
              to={link.to}
              className={`block w-full py-6 text-2xl font-extrabold text-gray-900 text-right border-b border-zinc-200 hover:text-[#007AFF] transition ${location.pathname === link.to ? 'text-blue-600' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
              style={{textShadow: '0 1px 8px rgba(255,255,255,0.18)'}}
            >
              {link.label}
            </Link>
          ))}
        </div>
      </nav>
    </>
  );
} 